/**
 * System-bound AI assistant (design: Admin Console › AI ASSISTANT; FR-AI-08, FR-DOC-05). Answers use only the
 * bound system's documents, cases and feedback, with citations. Quick actions: generate cases, coverage gaps,
 * summarise open feedback, Arabic content status.
 */
import type { AssistantAnswer, Citation, KnowledgeSummary } from '@uatp/shared';
import {
  Banner,
  Drawer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  IconButton,
  cn,
} from '@uatp/ui';
import { ChevronDown, FileText, Send, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { post } from '../api/client';
import { useAuth } from '../auth/auth';
import { errorMessage, useGet } from '../lib/query';
import { useScope } from '../lib/scope';
import { useSystems } from '../lib/systems';
import { useShell } from '../shell/store';
import { Markdown } from './Markdown';

interface Msg {
  role: 'user' | 'assistant';
  text: string;
  citations?: Citation[];
  error?: boolean;
}

type QuickAction = 'generate_cases' | 'coverage_gaps' | 'summarize_feedback' | 'arabic_status';

const ACTIONS: { key: QuickAction; label: string; help: (s: string) => string; prompt: string }[] = [
  { key: 'generate_cases', label: 'Generate test cases', help: (s) => `Draft cases from ${s}’s requirements and use cases.`, prompt: '' },
  { key: 'coverage_gaps', label: 'Find coverage gaps', help: () => 'Requirements and use cases with no test case.', prompt: 'Where are the coverage gaps?' },
  { key: 'summarize_feedback', label: 'Summarize open feedback', help: () => 'What blocks the current session.', prompt: 'What feedback is open and what blocks go-live?' },
  { key: 'arabic_status', label: 'Arabic content status', help: () => 'Which cases still need Arabic translations.', prompt: 'Which cases are missing Arabic content?' },
];

export function AiAssistantDrawer() {
  const { aiOpen, aiSystemId, closeAi } = useShell();
  const scopeSystemId = useScope((s) => s.systemId);
  const { can } = useAuth();
  const navigate = useNavigate();
  const systems = useSystems(aiOpen);
  const [systemId, setSystemId] = useState<string | null>(null);
  const [threads, setThreads] = useState<Record<string, { conversationId: string | null; messages: Msg[] }>>({});
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Bind to the page's system, else the header scope, else the first visible system.
  useEffect(() => {
    if (!aiOpen) return;
    const list = systems.data?.items ?? [];
    const pick = [aiSystemId, scopeSystemId, systemId].find((id) => id && list.some((s) => s.id === id)) ?? list[0]?.id ?? null;
    if (pick !== systemId) setSystemId(pick);
  }, [aiOpen, aiSystemId, scopeSystemId, systems.data, systemId]);

  const system = systems.systems.find((s) => s.id === systemId);
  const knowledge = useGet<KnowledgeSummary>(aiOpen && systemId ? `/systems/${systemId}/knowledge` : null);
  const thread = (systemId && threads[systemId]) || { conversationId: null, messages: [] };

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [thread.messages.length, thinking]);

  const ask = async (message: string, quickAction: QuickAction | null = null) => {
    if (!systemId || !message.trim() || thinking) return;
    const sid = systemId;
    const push = (m: Msg, conversationId?: string | null) =>
      setThreads((t) => {
        const cur = t[sid] ?? { conversationId: null, messages: [] };
        return { ...t, [sid]: { conversationId: conversationId === undefined ? cur.conversationId : conversationId, messages: [...cur.messages, m] } };
      });
    push({ role: 'user', text: message });
    setInput('');
    setThinking(true);
    try {
      const res = await post<AssistantAnswer>('/ai/assistant', { systemId: sid, message, conversationId: thread.conversationId, quickAction });
      push({ role: 'assistant', text: res.answer, citations: res.citations }, res.conversationId);
    } catch (e) {
      push({ role: 'assistant', text: errorMessage(e), error: true });
    } finally {
      setThinking(false);
      inputRef.current?.focus();
    }
  };

  const runAction = (a: (typeof ACTIONS)[number]) => {
    if (a.key === 'generate_cases') {
      closeAi();
      navigate(`/library?generate=1${systemId ? `&systemId=${systemId}` : ''}`);
      return;
    }
    void ask(a.prompt, a.key);
  };

  const docs = knowledge.data?.indexedDocuments ?? 0;
  const name = system?.name ?? 'this system';

  return (
    <Drawer
      open={aiOpen}
      onOpenChange={(o) => !o && closeAi()}
      title={
        <span className="inline-flex items-center gap-2.5">
          <Sparkles className="size-[18px] text-primary" aria-hidden />
          AI assistant
        </span>
      }
      width={560}
      bodyClassName="p-0 gap-0"
    >
      <div className="flex shrink-0 flex-wrap items-center gap-2.5 border-b border-border bg-surface-2 px-5 py-3">
        <span className="text-12 font-semibold text-text-2">Bound to</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-surface px-2.5 text-13 font-semibold text-text hover:bg-surface-2"
              aria-label={`Bound to ${name}. Change system`}
              disabled={!systems.systems.length}
            >
              <span className={cn('size-2 rounded-full', docs ? 'bg-success' : 'bg-warning')} aria-hidden />
              {system?.name ?? 'No system'}
              <ChevronDown className="size-3 text-text-3" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[260px]">
            <DropdownMenuRadioGroup value={systemId ?? ''} onValueChange={setSystemId}>
              {(systems.systems).map((s) => (
                <DropdownMenuRadioItem key={s.id} value={s.id}>
                  <span className="flex-1 font-medium">{s.name}</span>
                  <span className="text-11 text-text-3">{s.documentCount} docs</span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="text-12 text-text-2">
          {knowledge.data
            ? docs
              ? `${docs} documents · ${knowledge.data.cases} cases · ${knowledge.data.modules} modules indexed`
              : `${knowledge.data.cases} cases · no documents indexed`
            : ' '}
        </span>
        <span className="flex-1" />
        {systemId && (
          <button
            type="button"
            className="text-12 font-medium text-primary-text hover:underline"
            onClick={() => {
              closeAi();
              navigate(`/systems/${systemId}?tab=documents`);
            }}
          >
            Manage documents
          </button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-auto px-5 py-4 text-13" aria-live="polite">
        {!systems.isPending && !systems.systems.length && (
          <Banner variant="info">You don’t have access to any systems yet. Ask your global admin to grant you one.</Banner>
        )}
        {knowledge.data && !docs && (
          <Banner variant="warning" title={`No documents indexed for ${name}.`}>
            Answers fall back to the test library only. Upload an SRS or use cases under Systems → {name} → Documents.
          </Banner>
        )}
        <div className="grid grid-cols-2 gap-2">
          {ACTIONS.filter((a) => a.key !== 'generate_cases' || can('testCases', 'create')).map((a) => (
            <button
              key={a.key}
              type="button"
              disabled={!systemId || thinking}
              onClick={() => runAction(a)}
              className="flex min-w-0 flex-col items-start gap-1 rounded-md border border-border bg-surface px-3 py-2.5 text-start hover:border-primary hover:bg-primary-soft/40 disabled:opacity-60"
            >
              <span className="text-13 font-semibold">{a.label}</span>
              <span className="text-12 leading-4 text-text-2">{a.help(name)}</span>
            </button>
          ))}
        </div>
        {thread.messages.map((m, i) => (
          <div key={i} className={cn('flex flex-col gap-1', m.role === 'user' ? 'items-end' : 'items-start')}>
            <div
              className={cn(
                'max-w-[88%] rounded-[10px] px-3 py-2.5 leading-[19px]',
                m.role === 'user' ? 'whitespace-pre-wrap bg-primary-solid text-white' : m.error ? 'bg-error-soft text-error-text' : 'bg-surface-2 text-text',
              )}
            >
              {m.role === 'assistant' && !m.error ? <Markdown text={m.text} /> : m.text}
            </div>
            {!!m.citations?.length && (
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {m.citations.map((c, j) => (
                  <span key={j} className="inline-flex items-center gap-1 text-11 font-medium text-primary-text">
                    <FileText className="size-[11px]" aria-hidden />[{j + 1}] {c.documentName}
                    {c.section ? ` · ${c.section}` : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {thinking && <div className="text-12 text-text-3">Reading {name} documents…</div>}
        <div ref={endRef} />
      </div>

      <form
        className="flex shrink-0 flex-col gap-1.5 border-t border-border px-5 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          void ask(input);
        }}
      >
        <div className="flex gap-1.5">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Ask about ${name}…`}
            aria-label={`Ask about ${name}`}
            maxLength={4000}
            className="h-9 min-w-0 flex-1 rounded-md border border-border bg-surface px-3 text-13 text-text outline-none focus-visible:border-primary"
          />
          <IconButton type="submit" variant="primary" aria-label="Send" disabled={!input.trim() || !systemId || thinking} className="size-9">
            <Send />
          </IconButton>
        </div>
        <span className="text-11 text-text-3">Answers use only {name}’s documents, cases and feedback. AI can make mistakes — check the cited section.</span>
      </form>
    </Drawer>
  );
}
